import { FormEvent, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CheckCircle2, Info, KeyRound, Plus, RotateCw, Save, ShieldAlert, Trash2 } from 'lucide-react';

import AirBridgeMark from '../../components/AirBridgeMark';
import CopyValueButton from '../../components/CopyValueButton';
import RelativeTime from '../../components/RelativeTime';
import SectionCard from '../../components/SectionCard';
import GrantPermissionsButton from '../../components/tenant/GrantPermissionsButton';
import TenantApplicationDialog from '../../components/tenant/TenantApplicationDialog';
import { Fact, Mono } from '../../components/detailPrimitives';
import {
  SetTenantApplicationResult,
  TenantAppCertificateView,
  TenantDetail,
  setTenantApplication,
} from '../../api/tenantRegistry';
import { TenantAppAction } from '../../hooks/useTenantAppSignIn';
import { useTenantDetailQuery } from '../../hooks/useTenantDetail';
import { errorText } from '../../lib/errorText';
import { STATUS, STROKE, SURFACE, TEXT } from '../../theme/tokens';

// Application tab — FD-530 / FD-531 (`FD-ADR-046`).
//
// DESIGN DIRECTION: the same declaration-beside-report split the Service accounts
// tab carries, applied to the credential AirBridge authenticates with rather than
// the one an operator signs in as.
//
// WHY THIS TAB EXISTS AT ALL. Under `FD-ADR-045` §3 the FD-ADR-020 tenant
// application, for the Rubix-hosted tiers, lives in the CUSTOMER'S tenant — created
// from a Rubix-controlled template, owned by them, revocable by them without
// involving Rubix. So "which application is this tenant using" stops being a
// deployment-wide constant and becomes a per-tenant fact with three legitimate
// answers, and on a hosted deployment the difference between two of them is the
// difference between the customer's application and Rubix's.
//
// THE COPY IS THE HARD PART, AND IT IS WHY THIS FILE CARRIES A STATE MACHINE.
// `TenantServiceAccountsPage` learned this first: "not configured" is several
// different situations demanding different operator actions, and one string for all
// of them is how a WORKING deployment gets "fixed" by someone who thought it was
// broken.
//
//   using-default  — no per-tenant value; the deployment-wide application applies.
//                    Fine on Self-hosted, where that application IS the customer's.
//                    The copy NAMES it, because the next question is always "which".
//   unconfigured   — no value anywhere. Every Graph operation against this tenant
//                    fails, and the copy says so rather than implying it is pending.
//   set            — a per-tenant application is on file.
//
// The server makes the first two distinguishable by NEVER substituting the
// deployment default into `tenantAppClientId` — it stays null, and the resolved
// application's identity appears only in the service-principal snapshot, because
// that probe asked about whichever application actually resolved. That asymmetry is
// load-bearing in both halves: FD-530 also had to fix the probe, which was filtering
// on the deployment-wide id from inside a per-tenant loop and so reported "not
// found" for correctly provisioned customer tenants.
//
// NOTHING HERE RENDERS CREDENTIAL MATERIAL. A client id, an object id and a Key
// Vault certificate NAME are configuration identifiers of the same class as the
// service-principal object ids already surfaced (ADR-023 §5). The certificate stays
// in the vault and no API returns it.

/** Where this tenant's application comes from — the three-state statement. */
export type TenantAppSource =
  | { kind: 'set'; clientId: string; certificateName: string | null; objectId: string | null; setBy: string | null; setUtc: string | null }
  | { kind: 'using-default'; clientId: string }
  | { kind: 'unconfigured' };

/**
 * Derived from the tenant view alone — no client-side guess and no second call.
 *
 * The per-tenant column is authoritative when present. When it is null the
 * deployment-wide application may still resolve, and the only place its identity
 * appears is the service-principal snapshot, because the refresh probed whichever
 * application `Resolve-TenantAppConfiguration` resolved. A null column with no
 * probed service principal is the genuinely-nothing case.
 *
 * DO NOT "improve" this by reconstructing the deployment default client-side. That
 * would be a second resolver, which is exactly how the console comes to disagree
 * with the code that actually mints the token.
 */
export function tenantAppSource(tenant: TenantDetail): TenantAppSource {
  const declared = tenant.tenantAppClientId?.trim();
  if (declared) {
    return {
      kind: 'set',
      clientId: declared,
      certificateName: tenant.tenantAppCertificateName,
      objectId: tenant.tenantAppObjectId,
      setBy: tenant.tenantAppSetBy,
      setUtc: tenant.tenantAppSetUtc,
    };
  }
  const probed = tenant.servicePrincipals[0]?.appId?.trim();
  if (probed) return { kind: 'using-default', clientId: probed };
  return { kind: 'unconfigured' };
}

/**
 * The certificate state as a badge. FOUR values, never collapsed — `unknown` is
 * deliberately NOT rendered as healthy, because "we cannot tell" must not read as
 * "fine" for a credential.
 */
export function CertificateStateBadge({ state }: { state: TenantAppCertificateView['state'] }) {
  if (state === 'healthy') {
    return (
      <Badge appearance="tint" color="success">
        Current
      </Badge>
    );
  }
  if (state === 'due') {
    // Amber, not red: a certificate that has not been rotated yet is "look at
    // this", and red stays reserved for a real failure (FLUENT_UI_ADDENDUM triage).
    return (
      <Badge appearance="tint" color="warning">
        Rotation due
      </Badge>
    );
  }
  if (state === 'unknown') {
    return (
      <Badge appearance="tint" color="warning">
        Expiry unknown
      </Badge>
    );
  }
  return (
    <Badge appearance="tint" color="subtle">
      Not configured
    </Badge>
  );
}

export default function TenantApplicationPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const query = useTenantDetailQuery(tenantId);
  const tenant = query.data;
  if (!tenant || !tenantId) return null;

  return <ApplicationBody tenantId={tenantId} tenant={tenant} />;
}

function ApplicationBody({ tenantId, tenant }: { tenantId: string; tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const source = tenantAppSource(tenant);
  const cert = tenant.tenantAppCertificate;

  const [dialogAction, setDialogAction] = useState<TenantAppAction | null>(null);
  const [clientDraft, setClientDraft] = useState('');
  const [certDraft, setCertDraft] = useState('');
  const [objectDraft, setObjectDraft] = useState('');
  const [saved, setSaved] = useState<SetTenantApplicationResult | null>(null);

  // A named application that the last Graph check did not see.
  //
  // WORDED AS AN OBSERVATION WITH ITS TIMESTAMP, not as a finding of absence, and
  // that restraint is deliberate: the refresh PRESERVES its last-good snapshot when
  // a Graph read fails, so an empty list is ambiguous between "not there" and "we
  // could not look". Claiming the first would manufacture a false alarm; saying what
  // was actually observed, beside when, is the FD-ADR-023 §3 honest-staleness rule.
  const namedButUnseen =
    source.kind === 'set' &&
    !tenant.servicePrincipals.some((sp) => sp.appId?.trim() === source.clientId);

  const save = useMutation<SetTenantApplicationResult, unknown, void>({
    mutationFn: () =>
      setTenantApplication(tenantId, clientDraft.trim(), certDraft.trim(), objectDraft.trim()),
    onSuccess: (result) => {
      // Prime rather than refetch: the PATCH already ran the server's own follow-on
      // refresh and returned the re-read view, so seeding the shared cache updates
      // the facts above in the same paint. Refetching would let the two disagree on
      // screen for a round trip.
      queryClient.setQueryData(['tenant', tenantId], result.tenant);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setSaved(result);
      setClientDraft('');
      setCertDraft('');
      setObjectDraft('');
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!clientDraft.trim() || !certDraft.trim() || save.isPending) return;
    setSaved(null);
    save.mutate();
  }

  const tenantLabel = tenant.tenantDomain?.trim() || tenant.displayName?.trim() || tenant.tenantId;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 120, damping: 20 }}
      className="flex flex-col gap-6"
    >
      {/* FD-570 — what this is, in the customer's words, beside whether it is ready.
          No internal vocabulary: an ADR number or a task id means nothing to the
          admin reading this, and it is the admin who decides whether to trust it. */}
      <SectionCard className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
              style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
            >
              <AirBridgeMark size={18} />
            </div>
            <div>
              <h2 className="m-0 font-display text-lg font-semibold" style={{ color: TEXT.primary }}>
                AirBridge application
              </h2>
              <p className="m-0 mt-1 font-display text-sm" style={{ color: TEXT.secondary }}>
                The application AirBridge signs in as to work in this tenant during migrations.
              </p>
            </div>
          </div>

          <div
            className="flex gap-3 rounded-lg p-4"
            style={{ border: `1px solid ${STROKE.base}`, backgroundColor: STATUS.underwayTint }}
          >
            <Info size={18} strokeWidth={1.5} className="mt-0.5 shrink-0" style={{ color: STATUS.underwayText }} />
            <div className="font-display text-[13px] leading-relaxed" style={{ color: TEXT.primary }}>
              <div className="font-semibold">What is this used for?</div>
              AirBridge uses this application for the tenant-side steps of a migration — for example
              removing a migrated device&rsquo;s old Intune and Autopilot records, and registering it
              in its new tenant. It lives in your directory and belongs to your organisation, so you
              can review or remove it at any time. Only identifiers are shown here; the
              certificate&rsquo;s key never leaves your Key Vault.
            </div>
          </div>
        </div>

        <StatusPanel
          tenant={tenant}
          source={source}
          namedButUnseen={namedButUnseen}
          onCreate={() => {
            setSaved(null);
            setDialogAction('create');
          }}
        />
      </SectionCard>

      <SectionCard className="flex flex-col gap-8">
        <section className="flex flex-col gap-4">
          <SubHeading>Application details</SubHeading>

          <SourceStatement source={source} />

          {namedButUnseen && (
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Not seen in the last check</MessageBarTitle>
                The last check did not find application{' '}
                <span className="font-mono text-xs">{source.kind === 'set' ? source.clientId : ''}</span>{' '}
                in this tenant (checked{' '}
                <RelativeTime iso={tenant.refreshedUtc} emptyPlaceholder="never" />). Either it has
                not been created there, the client ID is wrong, or the check itself failed — and a
                failed check keeps the previous result, so this does not by itself prove absence. Use{' '}
                <strong>Re-check</strong> above, then act on what it says.
              </MessageBarBody>
            </MessageBar>
          )}

          <ApplicationActions
            source={source}
            cert={cert}
            onAction={(action) => {
              setSaved(null);
              setDialogAction(action);
            }}
          />
        </section>

        <section className="flex flex-col gap-4 border-t pt-6" style={{ borderColor: STROKE.base }}>
          <SubHeading>Certificate</SubHeading>
          <CertificateFacts tenant={tenant} cert={cert} />
        </section>

        {/* Record an existing application — kept separate from creating one, so an
            operator who already holds a client ID is not sent through a sign-in they
            do not need. */}
        <section className="flex flex-col gap-4 border-t pt-6" style={{ borderColor: STROKE.base }}>
          <div>
            <SubHeading>Record an existing application</SubHeading>
            <p className="m-0 mt-1 max-w-[75ch] font-display text-sm" style={{ color: TEXT.secondary }}>
              For an application created outside AirBridge. The client ID and the certificate name
              are required together: half a record would pair this tenant&rsquo;s application with
              the wrong certificate, which fails as an unhelpful sign-in error later.
            </p>
          </div>

          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="Application (client) ID" hint="A GUID, from the application's Overview page in Entra.">
                <Input
                  value={clientDraft}
                  placeholder={source.kind === 'set' ? source.clientId : '00000000-0000-0000-0000-000000000000'}
                  onChange={(_, data) => setClientDraft(data.value)}
                  disabled={save.isPending}
                />
              </Field>
              <Field label="Key Vault certificate name" hint="The certificate's name in Key Vault — never the certificate itself.">
                <Input
                  value={certDraft}
                  placeholder={source.kind === 'set' ? (source.certificateName ?? 'certificate-name') : 'certificate-name'}
                  onChange={(_, data) => setCertDraft(data.value)}
                  disabled={save.isPending}
                />
              </Field>
              <Field
                label="Application object ID (optional)"
                hint="Not the client ID — it is what certificate rotation uses."
              >
                <Input
                  value={objectDraft}
                  placeholder={source.kind === 'set' ? (source.objectId ?? '') : ''}
                  onChange={(_, data) => setObjectDraft(data.value)}
                  disabled={save.isPending}
                />
              </Field>
            </div>
            <div>
              <Button
                type="submit"
                appearance="secondary"
                icon={<Save size={16} strokeWidth={1.5} />}
                disabled={save.isPending || !clientDraft.trim() || !certDraft.trim()}
              >
                {save.isPending ? 'Saving…' : source.kind === 'set' ? 'Replace application' : 'Record application'}
              </Button>
            </div>
          </form>

          {save.isError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not record the application</MessageBarTitle>
                {errorText(save.error)}
              </MessageBarBody>
            </MessageBar>
          )}

          {saved !== null && (
            <MessageBar intent={saved.refreshed ? 'success' : 'warning'}>
              <MessageBarBody>
                {saved.refreshed ? (
                  <>
                    <MessageBarTitle>Application recorded</MessageBarTitle>
                    Recorded by {saved.setBy} and re-checked against the tenant.
                  </>
                ) : (
                  <>
                    <MessageBarTitle>Recorded — snapshot not re-checked</MessageBarTitle>
                    {/* `refreshed: false` with a 200 is a SUCCESSFUL write whose
                        follow-on probe failed. No retry affordance deliberately:
                        retrying would re-PATCH a write that already landed. */}
                    The application was stored. The details above are still from the previous
                    check — use <strong>Re-check</strong> for a fresh one.
                  </>
                )}
              </MessageBarBody>
            </MessageBar>
          )}
        </section>
      </SectionCard>

      {dialogAction !== null && (
        <TenantApplicationDialog
          open
          action={dialogAction}
          tenantId={tenantId}
          tenantLabel={tenantLabel}
          onClose={() => setDialogAction(null)}
        />
      )}
    </motion.div>
  );
}

function SubHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="m-0 font-display text-[15px] font-semibold" style={{ color: TEXT.primary }}>
      {children}
    </h3>
  );
}

/**
 * The status card beside the explanation: is this tenant ready, and if not, the one
 * thing to do next. Every state names an action or says there is none.
 */
function StatusPanel({
  tenant,
  source,
  namedButUnseen,
  onCreate,
}: {
  tenant: TenantDetail;
  source: TenantAppSource;
  namedButUnseen: boolean;
  onCreate: () => void;
}) {
  const shell = (tone: 'success' | 'warning' | 'neutral', icon: ReactNode, title: string, body: ReactNode, action?: ReactNode) => (
    <div
      className="flex flex-col gap-3 rounded-lg p-4"
      style={{
        border: `1px solid ${STROKE.base}`,
        backgroundColor: tone === 'success' ? STATUS.successTint : tone === 'warning' ? STATUS.warningTint : SURFACE.rowFill,
      }}
      data-testid="application-status"
    >
      <div className="flex items-start gap-3">
        {icon}
        <div className="font-display">
          <div
            className="text-sm font-semibold"
            style={{ color: tone === 'success' ? STATUS.successText : tone === 'warning' ? STATUS.warningText : TEXT.primary }}
          >
            {title}
          </div>
          <div className="mt-1 text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
            {body}
          </div>
        </div>
      </div>
      {action}
    </div>
  );

  if (source.kind === 'unconfigured') {
    return shell(
      'warning',
      <ShieldAlert size={20} strokeWidth={1.5} style={{ color: STATUS.warningText }} />,
      'No application yet',
      'AirBridge cannot work in this tenant until it has one. Creating it takes one sign-in by an administrator of this tenant.',
      <div>
        <Button appearance="primary" icon={<Plus size={16} strokeWidth={1.5} />} onClick={onCreate}>
          Create application in this tenant
        </Button>
      </div>,
    );
  }

  if (source.kind === 'using-default') {
    return shell(
      'neutral',
      <Info size={20} strokeWidth={1.5} style={{ color: TEXT.secondary }} />,
      'Using the deployment’s application',
      'Correct for a self-hosted deployment. On a Rubix-hosted one, create or record an application for this tenant.',
      <div>
        <Button appearance="secondary" icon={<Plus size={16} strokeWidth={1.5} />} onClick={onCreate}>
          Create application in this tenant
        </Button>
      </div>,
    );
  }

  if (tenant.consentStatus === 'consented' && !namedButUnseen) {
    return shell(
      'success',
      <CheckCircle2 size={20} strokeWidth={1.5} style={{ color: STATUS.successText }} />,
      'Application is configured',
      'This tenant is connected and ready to use.',
    );
  }

  return shell(
    'warning',
    <ShieldAlert size={20} strokeWidth={1.5} style={{ color: STATUS.warningText }} />,
    tenant.consentStatus === 'unknown' ? 'Permissions not confirmed' : 'Permissions not granted yet',
    'A Global Administrator or Privileged Role Administrator of this tenant approves the application’s permissions. You come straight back here.',
    tenant.grantUrl || tenant.consentUrl ? (
      <div>
        <GrantPermissionsButton tenant={tenant} />
      </div>
    ) : undefined,
  );
}

/**
 * The three-state statement. Each branch names a different next action, which is
 * the entire reason they are not one string.
 */
function SourceStatement({ source }: { source: TenantAppSource }) {
  if (source.kind === 'set') {
    return (
      <dl className="grid grid-cols-1 gap-x-16 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="Application (client) ID">
          <span className="inline-flex items-center gap-1">
            <Mono>{source.clientId}</Mono>
            <CopyValueButton value={source.clientId} label="application client ID" />
          </span>
        </Fact>
        <Fact label="Object ID">
          {source.objectId ? (
            <span className="inline-flex items-center gap-1">
              <Mono>{source.objectId}</Mono>
              <CopyValueButton value={source.objectId} label="application object ID" />
            </span>
          ) : (
            // Ordinary, not a fault — and named as what it blocks rather than
            // rendered as a bare dash somebody has to interpret.
            <span className="text-zinc-500 text-sm">Not recorded — rotation needs it</span>
          )}
        </Fact>
        <Fact label="Recorded by">
          {source.setBy ? (
            <span className="text-zinc-800">{source.setBy}</span>
          ) : (
            <span className="text-zinc-400">—</span>
          )}
          <div className="text-zinc-500 text-xs mt-1">
            <RelativeTime iso={source.setUtc} emptyPlaceholder="—" />
          </div>
        </Fact>
      </dl>
    );
  }

  if (source.kind === 'using-default') {
    return (
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>No application set — using the deployment-wide one</MessageBarTitle>
          This tenant names no application of its own, so the deployment-wide application applies:{' '}
          <span className="font-mono text-xs">{source.clientId}</span>. That is correct for a
          self-hosted deployment, where that application already belongs to this customer. On a
          Rubix-hosted deployment it does not — record or create a per-tenant application there.
        </MessageBarBody>
      </MessageBar>
    );
  }

  return (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>No application anywhere</MessageBarTitle>
        Neither this tenant nor this deployment has an application, so AirBridge cannot sign in
        to this tenant at all — checks, migration steps and wave activation will all fail until
        one exists. Create one, or record an existing one below.
      </MessageBarBody>
    </MessageBar>
  );
}

/** The brokered actions, gated on what the current state actually permits. */
function ApplicationActions({
  source,
  cert,
  onAction,
}: {
  source: TenantAppSource;
  cert: TenantAppCertificateView;
  onAction: (action: TenantAppAction) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {source.kind === 'set' && (
          <Button
            appearance="primary"
            icon={<RotateCw size={16} strokeWidth={1.5} />}
            onClick={() => onAction('rotate')}
            disabled={!source.objectId}
          >
            Rotate certificate
          </Button>
        )}
        {cert.removalPending && (
          <Button
            appearance="secondary"
            icon={<Trash2 size={16} strokeWidth={1.5} />}
            onClick={() => onAction('remove-previous-key')}
          >
            Remove superseded key
          </Button>
        )}
      </div>

      <p className="max-w-[65ch] font-display text-xs" style={{ color: TEXT.secondary }}>
        {/* Stated everywhere an operator meets these buttons, because it is the
            fact that explains why none of this happens on a schedule. */}
        Changing the application needs an <strong>Application Administrator of this tenant</strong>{' '}
        to sign in. AirBridge keeps no standing access to applications in your directory, so nothing
        here can run unattended — which is why the certificate expiry is something to watch rather
        than something that takes care of itself.
      </p>
    </div>
  );
}

function CertificateFacts({
  tenant,
  cert,
}: {
  tenant: TenantDetail;
  cert: TenantAppCertificateView;
}) {
  if (cert.state === 'not-configured') {
    return (
      <p className="max-w-[65ch] font-display text-sm" style={{ color: TEXT.secondary }}>
        No application yet, so there is no certificate to report on.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <dl className="grid grid-cols-1 gap-x-16 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="State">
          <CertificateStateBadge state={cert.state} />
        </Fact>
        <Fact label="Expires">
          {cert.expiresUtc ? (
            <span className="text-zinc-800">
              <RelativeTime iso={cert.expiresUtc} emptyPlaceholder="—" />
              {cert.daysRemaining !== null && (
                <span className="text-zinc-500">
                  {' '}
                  ({cert.daysRemaining < 0
                    ? `${Math.abs(cert.daysRemaining)} days ago`
                    : `${cert.daysRemaining} days`})
                </span>
              )}
            </span>
          ) : (
            // NOT "—". An application on file whose expiry is not known is the
            // ordinary state for one recorded through PATCH, and it must not render
            // as either healthy or as missing data.
            <span className="text-zinc-600 text-sm">
              Not known — recorded without one, so its expiry is not tracked
            </span>
          )}
        </Fact>
        <Fact label="Key Vault certificate">
          {tenant.tenantAppCertificateName ? (
            <span className="inline-flex items-start gap-1">
              <Mono>{tenant.tenantAppCertificateName}</Mono>
              <CopyValueButton value={tenant.tenantAppCertificateName} label="certificate name" />
            </span>
          ) : (
            <span className="text-zinc-400">—</span>
          )}
        </Fact>
      </dl>

      {/* The last rotation rides ALONGSIDE the expiry state rather than instead of
          it: they answer different questions, and due-AND-rotation-failed is the
          combination that matters most. */}
      {cert.rotationStatus === 'failed' && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>The last rotation did not complete</MessageBarTitle>
            Attempted <RelativeTime iso={cert.rotationUtc} emptyPlaceholder="—" />
            {cert.rotationError ? `: ${cert.rotationError}` : '.'} The recorded certificate was left
            exactly as it was, so the application still authenticates with the key it had — a failed
            rotation changes nothing rather than pointing at a certificate the tenant has never been
            told about. Rotate again.
          </MessageBarBody>
        </MessageBar>
      )}

      {cert.removalPending && (
        <MessageBar intent="info" icon={<KeyRound size={16} strokeWidth={1.5} />}>
          <MessageBarBody>
            <MessageBarTitle>A superseded key is still attached</MessageBarTitle>
            Key <span className="font-mono text-xs">{cert.previousKeyId}</span> was replaced and left
            in place on purpose — a token minted just before the rotation is still valid and still
            being presented. <strong>Removing it is optional</strong>: left alone it expires on its
            own. Remove it early when the rotation followed a suspected compromise.
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
